/**
 * EinstufungProgressContext — broadcasts the live progress of the
 * Einstufung-Tool so the UserLayout sidebar can render a "Wizard-Live-View"
 * (5 stacked cards from top → bottom): Stichtag, Tätigkeit, KI-Vorschlag,
 * Anrechenbare Dienstjahre, Endgültige Empfehlung.
 *
 * Progressive Disclosure: cards mount only once their value is set
 * (no "—" placeholders). The Empfehlung card additionally exposes a
 * one-click PDF download via the callback that StepFour registers
 * after a successful recommendation run.
 *
 * No persistence — state lives only in memory and resets on
 * `resetProgress()` (triggered by the "Neue Einstufung" workflow).
 */
import React, { createContext, useContext, useState, useCallback, useMemo } from "react";

const EMPTY = {
  stichtag: null,             // ISO YYYY-MM-DD
  kvId: null,                 // UUID — Iter 19.65.10 for fetching KV-Details
  kvName: null,               // "KV Handel"
  kvKuerzel: null,            // "KV HANDEL"
  taetigkeitKurz: null,       // free text from Step 2 AI extraction
  qualifikation: null,        // free text
  suggestedGroup: null,       // "D"
  anrechenbareMonate: null,   // number, fractional months
  finalGroup: null,           // "D"
  finalStufe: null,           // number | string (KV may use "I"/"II")
};

const EinstufungProgressContext = createContext({
  progress: EMPTY,
  setProgress: () => {},
  resetProgress: () => {},
  pdfDownload: null,
  pdfBusy: false,
  registerPdfDownload: () => {},
  unregisterPdfDownload: () => {},
});

export function EinstufungProgressProvider({ children }) {
  const [progress, setProgressState] = useState(EMPTY);
  const [pdfDownload, setPdfDownload] = useState(null);
  const [pdfBusy, setPdfBusy] = useState(false);

  const setProgress = useCallback((patch) => {
    setProgressState((prev) => ({ ...prev, ...patch }));
  }, []);

  const resetProgress = useCallback(() => {
    setProgressState(EMPTY);
    setPdfDownload(null);
    setPdfBusy(false);
  }, []);

  const registerPdfDownload = useCallback((fn, busy) => {
    // Wrap so the consumer (sidebar) gets a stable identity check.
    setPdfDownload(() => fn);
    setPdfBusy(Boolean(busy));
  }, []);

  const unregisterPdfDownload = useCallback(() => {
    setPdfDownload(null);
    setPdfBusy(false);
  }, []);

  const value = useMemo(
    () => ({
      progress,
      setProgress,
      resetProgress,
      pdfDownload,
      pdfBusy,
      registerPdfDownload,
      unregisterPdfDownload,
    }),
    [progress, setProgress, resetProgress, pdfDownload, pdfBusy, registerPdfDownload, unregisterPdfDownload],
  );

  return (
    <EinstufungProgressContext.Provider value={value}>
      {children}
    </EinstufungProgressContext.Provider>
  );
}

export function useEinstufungProgress() {
  return useContext(EinstufungProgressContext);
}
